import { motion } from 'framer-motion';
import { useLanguage } from '@/context/LanguageContext';
import { TID } from '@/lib/testIds';
import { IMAGES } from '@/lib/images';
import { scrollToId } from '@/components/site/Navbar';
import { ArrowRight, Buildings, Flask, GraduationCap, Bank, GlobeHemisphereEast, Network } from '@phosphor-icons/react';

const PARTNER_ICONS = [Buildings, Flask, GraduationCap, Bank, GlobeHemisphereEast, Network];

export default function Partnerships() {
  const { t } = useLanguage();
  return (
    <section
      id="partnerships"
      data-testid={TID.partnerships.root}
      className="relative bg-[#0A1128] text-white overflow-hidden"
    >
      <div className="absolute inset-0 bg-grid-lines opacity-50 pointer-events-none" />

      {/* Partnership meeting feature image */}
      <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-20 md:pt-28">
        <div className="relative aspect-[21/9] w-full overflow-hidden border border-white/10">
          <img
            src={IMAGES.partnershipsMeeting}
            alt="Strategic defense partnership meeting"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A1128] via-[#0A1128]/30 to-transparent" />
          <div className="absolute top-4 left-4 right-4 flex items-center justify-between font-mono text-[10px] tracking-[0.25em] uppercase text-white/80">
            <span>{'COLLABORATION / SPECIALISTS'}</span>
            <span className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-[#4F6355] rounded-full" />
              ACTIVE
            </span>
          </div>
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.6 }}
              className="flex items-center gap-3"
            >
              <span className="w-8 h-px bg-[#4F6355]" />
              <span className="text-[11px] tracking-[0.3em] uppercase font-bold text-[#4F6355]">
                {t.partnerships.overline}
              </span>
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.7, delay: 0.05 }}
              className="font-display font-bold tracking-tighter mt-6 text-3xl sm:text-4xl lg:text-5xl text-white leading-[1.05]"
            >
              {t.partnerships.title}
            </motion.h2>

            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.7, delay: 0.12 }}
              className="mt-6 text-base md:text-lg text-[#94A3B8] leading-relaxed"
            >
              {t.partnerships.body}
            </motion.p>

            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="mt-4 text-sm md:text-base text-[#94A3B8]/80 leading-relaxed"
            >
              {t.partnerships.body2}
            </motion.p>

            <div className="mt-8">
              <button
                data-testid={TID.partnerships.cta}
                onClick={() => scrollToId('contact')}
                className="btn-primary inline-flex items-center gap-3 px-6 py-3.5 text-[11px] tracking-[0.22em] uppercase font-bold"
              >
                {t.cta.contactUs}
                <ArrowRight size={14} weight="bold" />
              </button>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/10 border border-white/10">
              {t.partnerships.partners.map((p, idx) => {
                const Icon = PARTNER_ICONS[idx] || Buildings;
                const num = String(idx + 1).padStart(2, '0');
                return (
                  <motion.div
                    key={p}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-80px' }}
                    transition={{ duration: 0.55, delay: idx * 0.06 }}
                    className="group relative bg-[#0A1128] p-6 md:p-8 hover:bg-[#151F32] transition-colors duration-300 min-h-[180px] flex flex-col justify-between"
                  >
                    <div className="flex items-start justify-between">
                      <Icon size={28} weight="duotone" className="text-[#4F6355] group-hover:text-white transition-colors" />
                      <span className="font-mono text-[10px] tracking-[0.3em] text-[#94A3B8]">P / {num}</span>
                    </div>
                    <div className="mt-4">
                      <h3 className="font-display font-semibold text-lg md:text-xl tracking-tight text-white">
                        {p}
                      </h3>
                      <span className="block mt-3 w-8 h-px bg-white/10 group-hover:bg-[#4F6355] group-hover:w-16 transition-all duration-500" />
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
